<template>
	<div>
		<NuxtLayout v-if="!oneTimeTokenPending">
			<NuxtPage />
		</NuxtLayout>
		<div v-else class="min-h-screen flex items-center justify-center">
			<div class="w-10 h-10 rounded-full border-4 border-dj-blue border-t-transparent animate-spin"></div>
		</div>
		<CookieConsent v-if="!oneTimeTokenPending" />
		<!--<LoadingScreen v-model="loading" />-->
		<ModalNewLegalDocs v-if="!loading && !oneTimeTokenPending" />

		<!-- SZMD Vote Modal. Show first landing. -->
		<ModalSzmdVote v-if="!oneTimeTokenPending" />

		<!-- SZMD Box Vote Modal -->
		<ModalSzmdBoxVote v-if="showSzmdBoxVoteModal && !oneTimeTokenPending" v-model="showSzmdBoxVoteModal" />

		<ChatlabWidget
			v-if="!oneTimeTokenPending && !hideC2CChatlabWidgetState"
			:margin-bottom="stickyBarHeight ? `${stickyBarHeight}px` : '0px'"
		/>
		<ChatlabWidget
			v-if="!oneTimeTokenPending && !hideB2BChatlabWidgetState"
			profile="b2b"
			:margin-bottom="stickyBarHeight ? `${stickyBarHeight}px` : '0px'"
		/>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onUpdated, onMounted, provide } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

export default defineComponent({
	setup() {
		const { locale } = useI18n();
		const loading = ref(true);
		const showCookieConsent = ref(true);
		const route = useRoute();
		const router = useRouter();
		const app = useNuxtApp();

		const isAuth = useState("isAuth");
		const oneTimeTokenPending = useState("oneTimeTokenPending", () => false);
		const showSzmdBoxVoteModal = useState("showSzmdBoxVoteModal", () => false);
		const stickyBarHeight = useState("stickyBarHeight", () => 0);

		// Function to check if the current route is the business prices page
		const isBusinessPricesRoute = (path: string) => /^\/[a-z]{2}\/business\/prices(?:\/|$)/i.test(path);

		// State to control the visibility of the ChatlabWidget
		const hideC2CChatlabWidgetState = ref(true);
		const hideB2BChatlabWidgetState = ref(true);

		// Variable to hold the requestAnimationFrame ID for canceling if needed
		let rafId: number | null = null;

		/**
		 * Synchronize the visibility of the Chatlab widgets based on the current route.
		 * @param {string} newPath - The new route path.
		 */
		const syncChatlabWidgetVisibility = (newPath: string) => {
			// Cancel any pending animation frame to avoid multiple checks
			if (rafId !== null) {
				cancelAnimationFrame(rafId);
				rafId = null;
			}

			// Hide both widgets initially
			if (isBusinessPricesRoute(newPath)) {
				hideC2CChatlabWidgetState.value = true;
			} else {
				hideB2BChatlabWidgetState.value = true;
			}

			// Function to check for the removal of the Chatlab widget element and update visibility accordingly
			const waitForElementRemoval = () => {
				const element = document.querySelector("#aichatbot_ra_div_id");
				if (element) {
					// If the element is still present, continue checking in the next animation frame
					rafId = requestAnimationFrame(waitForElementRemoval);
				} else {
					// Once the element is removed, update the visibility states based on the current route
					rafId = null;
					if (isBusinessPricesRoute(newPath)) {
						hideB2BChatlabWidgetState.value = false;
					} else {
						hideC2CChatlabWidgetState.value = false;
					}
				}
			};

			waitForElementRemoval();
		};

		// Scroll to top after navigation
		app.hook("page:finish", () => {
			window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
		});

		onUpdated(() => {
			const defaultLocale = useRegion.getCurrentRegion().defaultLang;
			const availableLocales = useRegion.getCurrentRegion().availableLangs;

			if (locale.value != defaultLocale && !availableLocales.includes(locale.value)) {
				if (!route.fullPath.includes(defaultLocale)) {
					const routeParts = route.fullPath.split("/");
					routeParts[1] = defaultLocale;
					const newRoute = routeParts.join("/");
					router.push(newRoute);
				}
				locale.value = defaultLocale;
			}

			loading.value = false;
		});

		onMounted(() => {
			useBanners.fetchBanners(locale.value, useRegion.getCurrentRegion().code);
			loading.value = false;

			// Watch for route changes
			watch(
				() => route.path,
				(newPath) => {
					syncChatlabWidgetVisibility(newPath);
				},
				{ immediate: true }
			);
		});

		function openSzmdBoxVoteModal() {
			const runtimeConfig = useRuntimeConfig();
			const showSzmdBox = runtimeConfig.public.activeConfig.szmdConfig.showBox;

			if (!showSzmdBox) {
				// FIX: The router push solution does not work on the first click.
				window.location.href = `/${locale.value}/szmd/rating`;
				return;
			}

			const showSzmdBoxVoteModal = useState<boolean>("showSzmdBoxVoteModal");
			showSzmdBoxVoteModal.value = true;
		}

		provide("openSzmdBoxVoteModal", openSzmdBoxVoteModal);

		useAuth.checkCookies();

		return {
			locale,
			loading,
			showCookieConsent,
			isAuth,
			oneTimeTokenPending,
			showSzmdBoxVoteModal,
			stickyBarHeight,
			hideC2CChatlabWidgetState,
			hideB2BChatlabWidgetState,
		};
	},
});
</script>
